import { Fragment, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { gql, useMutation, useQuery } from "@apollo/client"
import {
    getDayMonthYearFormatter,
    getSafeFormatter,
    getTimeFormatter,
} from "../../../i18n"
import { Banner } from "../../util/Banner"
import { Flash, WARNING } from "../../util/Flash"
import tableStyle from "../../../style/table.module.css"
import { Link, useHistory, useParams } from "react-router-dom"
import { TableHeader } from "../../util/table/TableHeader"
import { TableContentLoader } from "../../util/table/TableContentLoader"
import inputStyle from "../../../style/input.module.css"
import { uniqueId } from "lodash"
import style from "../home/Home.module.css"
import buttonStyle from "../../../style/button.module.css"
import c from "capitalize"
import { useDispatch } from "react-redux"
import { showError, showInfo } from "../../../redux/actions/NotificationActions"
import { Loader } from "../../util/Loader"
import { EnterNameModal } from "../../util/modals/EnterNameModal"
import cn from "classnames"
import { Pagination } from "../../util/table/Pagination"
import { EnumLabel } from "../../util/table/EnumLabel"
import { prisoBasePath, prisoBannerKey } from "./prisoUtils"

const GET_PRISO_LIST = gql`
    query (
        $id: UUID!
        $pageNum: Int
        $order: [PrisoListProductOrder]
    ) {
        me {
            name
            id
        }
        list: prisoList(id: $id) {
            id
            name
            prisoType
            isUploading
            chosenCalculation {
                id
            }
            calculation: prisoCalculations(limit: -1) {
                page {
                    id
                    name
                    calculationStatus
                }
            }
            products: prisoProducts(
                limit: 20
                pageNum: $pageNum
                orderBy: $order
            ) {
                currentPage
                hasNext
                hasPrevious
                pages
                page {
                    id
                    pzn
                    product {
                        id
                        pzn
                        name
                    }
                }
            }
        }
    }
`

const UPDATE_PRISO_LIST = gql`
    mutation (
        $id: UUID!
        $name: String
        $chosenCalculation: UUID
    ) {
        update: updatePrisoList(
            id: $id
            name: $name
            chosenCalculation: $chosenCalculation
        ) {
            id
            name
        }
    }
`

const TRIGGER_PRISO_RUN = gql`
    mutation ($id: UUID!, $name: String!) {
        trigger: triggerPrisoRun(name: $name, prisoListId: $id) {
            id
        }
    }
`

const RETRY_PRISO_RUN = gql`
    mutation ($id: UUID!) {
        retry: retryPrisoRun(id: $id) {
            id
            calculationStatus
        }
    }
`

const SEND_PRISO_EMAIL = gql`
    mutation ($id: UUID!) {
        sendPrisoAsEmail(id: $id)
    }
`

export const PrisoListEditor = ({ prisoType }) => {
    const { id } = useParams()

    const [name, setName] = useState("")
    const [chosenCalculation, setChosenCalculation] = useState("")

    const [unsavedChange, setUnsavedChange] = useState(false)

    const [name_input_id] = useState(() => uniqueId("name_input_"))
    const [chosenCalculation_id] = useState(() =>
        uniqueId("chosenCalculation_"),
    )

    const { t, i18n } = useTranslation(["common", "table", "page_names"])
    const dayMonthYearFormat = getSafeFormatter(
        getDayMonthYearFormatter(i18n.language),
    )
    const timeFormat = getSafeFormatter(getTimeFormatter(i18n.language))

    const basePath = prisoBasePath(prisoType)
    const bannerKey = prisoBannerKey(prisoType)

    const [namePickerShown, setNamePickerShown] = useState(false)

    const [pageNum, setPage] = useState(1)
    const [order, setOrder] = useState([])

    const { data, loading, startPolling, stopPolling } = useQuery(
        GET_PRISO_LIST,
        {
            variables: {
                id,
                order,
                pageNum,
            },
            skip: !id,
        },
    )

    useEffect(() => {
        if (data?.list?.isUploading) {
            startPolling(3000)
        } else {
            stopPolling()
        }
    }, [data?.list?.isUploading, startPolling, stopPolling])

    const [updatePrisoList, { loading: prisoListLoading }] = useMutation(
        UPDATE_PRISO_LIST,
        {
            refetchQueries: [GET_PRISO_LIST],
        },
    )

    const [triggerRun, { loading: triggerLoading }] =
        useMutation(TRIGGER_PRISO_RUN)

    const [retryRun, { loading: retryLoading }] = useMutation(RETRY_PRISO_RUN, {
        refetchQueries: [GET_PRISO_LIST],
    })

    const [sendEmail, { loading: emailLoading }] =
        useMutation(SEND_PRISO_EMAIL)

    useEffect(() => {
        setName(data?.list?.name || "")
        setChosenCalculation(data?.list?.chosenCalculation?.id || "")
    }, [data?.list])

    const dispatch = useDispatch()
    const history = useHistory()

    const canSendEmail =
        prisoType === "NEUANLAGE_KETTARTIKEL" || prisoType === "EK_AENDERUNG"

    return (
        <div>
            {namePickerShown ? (
                <EnterNameModal
                    text={t("table:name_priso_calculation")}
                    defaultText={`${c(
                        t("page_names:priso_calculations"),
                    )} ${timeFormat.format(new Date())} [${
                        data?.me?.name || ""
                    }]`}
                    onCancel={() => setNamePickerShown(false)}
                    loading={triggerLoading}
                    onNameSet={(calcName) => {
                        triggerRun({
                            variables: {
                                id,
                                name: calcName,
                            },
                        })
                            .then(() => {
                                setNamePickerShown(false)
                                history.push(`${basePath}/${id}`)
                            })
                            .catch((e) => {
                                setNamePickerShown(false)
                                dispatch(showError(e.message))
                            })
                    }}
                />
            ) : null}
            <Banner
                text={`${t(`page_names:${bannerKey}`)}: ${
                    data?.list?.name || ""
                }`}
            />
            {!loading && !data?.list ? (
                <Flash text={t("table:no_results")} type={WARNING} />
            ) : (
            <>
            <div className={tableStyle.stackedFilterBar}>
                {loading || prisoListLoading ? (
                    <Loader />
                ) : (
                    <div className={tableStyle.filterBar}>
                        <div className={tableStyle.filterInnerSection}>
                            <label
                                htmlFor={name_input_id}
                                className={tableStyle.label}
                            >
                                {t("table:name")}
                            </label>
                            <input
                                id={name_input_id}
                                className={inputStyle.text}
                                type="text"
                                value={name}
                                onChange={(evt) => setName(evt.target.value)}
                            />
                        </div>
                        {data?.list?.id ? (
                            <div className={tableStyle.filterInnerSection}>
                                <label
                                    htmlFor={chosenCalculation_id}
                                    className={tableStyle.label}
                                >
                                    {t("table:chosen_calculation")}
                                </label>
                                <select
                                    id={chosenCalculation_id}
                                    className={inputStyle.select}
                                    onChange={(evt) =>
                                        setChosenCalculation(evt.target.value)
                                    }
                                    value={chosenCalculation}
                                >
                                    <option value="" />
                                    {data?.list?.calculation?.page
                                        ?.filter(
                                            (calc) =>
                                                calc.calculationStatus ===
                                                "DONE",
                                        )
                                        .map((calc, i) => (
                                            <option key={i} value={calc.id}>
                                                {calc.name}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        ) : null}
                    </div>
                )}
                <div className={tableStyle.buttonSection}>
                    {data?.list?.id ? (
                        <button
                            className={buttonStyle.orangeButton}
                            title={
                                unsavedChange
                                    ? t("common:save_before_running")
                                    : null
                            }
                            disabled={loading || unsavedChange}
                            onClick={() => setNamePickerShown(true)}
                        >
                            {t("table:run_calculation")}
                        </button>
                    ) : null}

                    {canSendEmail && chosenCalculation ? (
                        <button
                            className={buttonStyle.lightBlueButton}
                            disabled={emailLoading}
                            onClick={() =>
                                sendEmail({
                                    variables: { id: chosenCalculation },
                                })
                                    .then(() =>
                                        dispatch(
                                            showInfo(
                                                t("common:email_sent"),
                                            ),
                                        ),
                                    )
                                    .catch((e) =>
                                        dispatch(showError(e.message)),
                                    )
                            }
                        >
                            {c(t("table:send_email"))}
                        </button>
                    ) : null}

                    {data?.list?.calculation?.page?.length ? (
                        <button
                            className={buttonStyle.lightBlueButton}
                            onClick={() =>
                                document
                                    .getElementById("priso-calculations")
                                    ?.scrollIntoView({ behavior: "smooth" })
                            }
                        >
                            {c(t("table:see_calculations"))}
                        </button>
                    ) : null}

                    <button
                        className={buttonStyle.button}
                        disabled={!name}
                        onClick={() =>
                            updatePrisoList({
                                variables: {
                                    id,
                                    name,
                                    chosenCalculation:
                                        chosenCalculation || null,
                                },
                            })
                                .then((r) => {
                                    history.push(
                                        `${basePath}/${r?.data?.update?.id}`,
                                    )
                                    setUnsavedChange(false)
                                })
                                .catch((e) => dispatch(showError(e.message)))
                        }
                    >
                        {t("table:save")}
                    </button>
                    <Link
                        to={`${basePath}/csv/${id}`}
                        className={buttonStyle.button}
                    >
                        {c(t("table:reupload_csv"))}
                    </Link>
                </div>
            </div>
            {!data?.list?.isUploading ? (
                <>
                    <h2 className={style.sectionHeading}>
                        {t("table:products")}
                    </h2>

                    {/* Calculation history */}
                    {data?.list?.calculation?.page?.length ? (
                        <>
                            <h2
                                id="priso-calculations"
                                className={style.sectionHeading}
                            >
                                {t("table:calculations")}
                            </h2>
                            <div
                                className={tableStyle.table}
                                style={{
                                    gridTemplateColumns: "2fr 1fr auto",
                                }}
                            >
                                <TableHeader
                                    className={tableStyle.header}
                                    label={t("table:name")}
                                />
                                <TableHeader
                                    className={tableStyle.header}
                                    label={t("table:status")}
                                />
                                <TableHeader />
                                {data?.list?.calculation?.page?.map((calc, i) => (
                                    <Fragment key={i}>
                                        {calc.calculationStatus === "DONE" ? (
                                            <Link
                                                className={cn(
                                                    tableStyle.cell,
                                                    tableStyle.linkCell,
                                                )}
                                                to={`${basePath}/${id}/calculations/${calc.id}`}
                                            >
                                                {calc.name}
                                            </Link>
                                        ) : (
                                            <p className={tableStyle.cell}>
                                                {calc.name}
                                            </p>
                                        )}
                                        <EnumLabel
                                            className={tableStyle.cell}
                                            label={calc.calculationStatus}
                                        />
                                        <p className={tableStyle.actionCell}>
                                            {["PENDING", "ABORTED"].includes(calc.calculationStatus) ? (
                                                <button
                                                    className={cn(
                                                        tableStyle.actionButton,
                                                        tableStyle.positiveActionButton,
                                                    )}
                                                    disabled={retryLoading}
                                                    onClick={() =>
                                                        retryRun({
                                                            variables: { id: calc.id },
                                                        }).catch((e) =>
                                                            dispatch(showError(e.message))
                                                        )
                                                    }
                                                >
                                                    {t("table:retry")}
                                                </button>
                                            ) : null}
                                        </p>
                                    </Fragment>
                                ))}
                            </div>
                        </>
                    ) : null}

                    {/* Product table */}
                    <div
                        className={`${tableStyle.table} ${tableStyle["table-cols-3"]}`}
                        style={{
                            gridTemplateColumns: "repeat(3, 1fr)",
                        }}
                    >
                        <TableHeader
                            className={tableStyle.header}
                            label={t("table:pzn")}
                            asc="PznAsc"
                            desc="PznDesc"
                            order={order}
                            setOrder={setOrder}
                        />
                        <TableHeader
                            className={tableStyle.header}
                            label={t("table:name")}
                            asc="ProductNameAsc"
                            desc="ProductNameDesc"
                            order={order}
                            setOrder={setOrder}
                        />
                        <p className={tableStyle.header} />
                        {(prisoListLoading || loading) && !data?.list && (
                            <TableContentLoader columns={3} />
                        )}
                        {data?.list?.products?.page.map((s, i) => (
                            <Fragment key={i}>
                                <p className={tableStyle.cell}>
                                    {s?.pzn || s?.product?.pzn}
                                </p>
                                <p className={tableStyle.cell}>
                                    {s?.product?.name || "-"}
                                </p>
                                <p
                                    className={cn(
                                        tableStyle.cell,
                                        tableStyle.lastCell,
                                    )}
                                />
                            </Fragment>
                        ))}
                    </div>
                    <Pagination
                        onPageChange={(page) => setPage(page)}
                        pagination={data?.list?.products}
                        loading={loading && !data?.list?.products}
                    />
                </>
            ) : (
                <Loader />
            )}
            </>
            )}
        </div>
    )
}
